// Målpeking i kameraet («target toggle»): to NYE peilings-/avstandsmetoder som
// utfyller `maalestimat.ts` (stråle mot DEM), for nettopp de tilfellene der DEM
// IKKE er svaret — se `vale/MAALPEKING_KONSEPT_2026-08-17.md` i sin helhet.
//
// ── HVORFOR DENNE FILA FINNES ────────────────────────────────────────────────
// Eierens scenario: ligge langs kysten, ta bilde av en båt. `estimerMaal()`
// (stråle mot DEM) svarer ikke her — mål på VANN har ingen terrengmodell å
// skjære, og en vannrett stråle over hav krysser aldri en flate i høyde 0 (den
// STIGER over den krummende havflata, se `krumningsloft` der). Konseptdokumentet
// fant to metoder som IKKE trenger DEM:
//
//   1. HORISONT + HAVFLATE (avstand): les depresjonen til målets vannlinje ved
//      å måle GAPET I PIKSLER mellom horisontlinja og vannlinja i SAMME bilde.
//      Horisontens egen dip-vinkel er kjent analytisk (kurvatur + refraksjon),
//      så depresjonen — og dermed avstanden — følger uten IMU-pitch og uten
//      DEM. Se §1 i konseptdokumentet.
//   2. LANDEMERKE-RESECTION (asimut): ett kjent, identifisert punkt i bildet
//      (fyrlykt, mast, kjent kai) løser monterings-asimuten ut av bildet selv,
//      i stedet for å stole på telefonens magnetometer (±20°, se funn 2 i
//      konseptdokumentet — retning er den DOMINERENDE feilkilden, ikke avstand).
//
// ⚠ GRUNNLOVEN §1, ORDRETT FOR DENNE FILA: «Vis ALDRI ett tall.» Hver
// eksporterte «svar»-funksjon returnerer avstand/asimut OG σ OG hvilken metode
// som ga dem — aldri et bart tall. Der metoden ikke kan svare (mål over
// horisonten, bølgene for store for øyehøyden, ingen landemerke identifisert),
// returneres en begrunnet avvisning — ALDRI en gjettet verdi.
//
// ── GJENBRUK, IKKE DUPLIKAT (§3) ─────────────────────────────────────────────
// Jordradius/refraksjon/horisontavstand: `ptz.ts` (`JORD_RADIUS_M`,
// `REFRAKSJON_K`, `effektivJordradiusM`, `horisontAvstandM`, `peiling360`).
// Piksel→vinkel (rektilineær projeksjon): `kamerakalibrering.ts`
// (`vinkelFraPikselKolonne`), brukt i REVERS her (kjent peiling → asimut, i
// stedet for kjent asimut → peiling). Peiling mellom to punkt: `targeting.ts`
// (`bearingDeg`). Usikkerhets-ellipsen (storakse/lilleakse/cepM) er samme form
// som `Treffusikkerhet` i `maalestimat.ts` — gjenbrukt for konsistens mellom
// ALLE ranging-metodene COP-en har, ikke en ny form per metode.
import { haversineM, project } from './geo.js';
import { bearingDeg } from './targeting.js';
import { effektivJordradiusM, horisontAvstandM, peiling360 } from './ptz.js';
import { vinkelFraPikselKolonne, vinkelFraPikselRad } from './kamerakalibrering.js';
/** Default når holdehøyden ikke er låst mot noe kjent (stativ, rekkverk). */
export const DEFAULT_HOLDEHOYDE_SIGMA_M = 0.25;
export function sammensattOyehoyde(input) {
    const bakke = { navn: 'Bakke/kai over datum', verdiM: input.bakkehoydeM, sigmaM: input.bakkehoydeSigmaM ?? 0.1 };
    const vann = { navn: 'Vannstand nå', verdiM: input.vannstandM, sigmaM: input.vannstandSigmaM ?? 0 };
    const hold = {
        navn: 'Holdehøyde',
        verdiM: input.holdehoydeM,
        sigmaM: input.holdehoydeSigmaM ?? DEFAULT_HOLDEHOYDE_SIGMA_M,
    };
    const ledd = [bakke, vann, hold];
    const hoydeM = bakke.verdiM - vann.verdiM + hold.verdiM; // øyet over VANNET: bakke minus hvor mye vannet har tatt av bakken, pluss holdehøyde
    const sigmaM = Math.sqrt(ledd.reduce((s, l) => s + l.sigmaM * l.sigmaM, 0));
    return { hoydeM, sigmaM, ledd };
}
// ═══════════════════════════════════════════════════════════════════════════
// 2. MARITIM METODE — horisont + havflate (avstand, analytisk, INGEN DEM)
// ═══════════════════════════════════════════════════════════════════════════
/** Terskel fra konseptdokumentet: under 10× signifikant bølgehøyde er horisont-
 *  fittet for bølgeforstyrret til å stole på — «avvis målingen når h < ~10·Hs». */
export const BOLGE_HOYDE_FORHOLD_MIN = 10;
/** Iterasjoner for krumnings-refinementet i `avstandFraDepresjon`. Konvergerer
 *  til under en centimeter godt innenfor 10 — se testen som viser det. */
const KRUMNING_ITERASJONER = 20;
/**
 * Horisontens dip-vinkel (grader) under sann horisontal, for en observatør i
 * `hoydeM`. Konseptdokumentets formel er dip = √(2h/R′); i stedet for å
 * reimplementere kvadratroten selv, hentes den ut av `horisontAvstandM` (JA,
 * DEN ER RIKTIG BRUKT SLIK): horisontavstanden er d₀ = R′·dip for små vinkler
 * (samme geometri, bare skrevet om), så dip = d₀/R′ — én kilde til sannhet
 * for R′ OG for kvadratroten, ingen ny formel å holde synkron med `ptz.ts`.
 */
export function horisontDipDeg(hoydeM, medRefraksjon = true) {
    if (hoydeM <= 0)
        return 0;
    const d0 = horisontAvstandM(hoydeM, 0, medRefraksjon);
    const rEff = effektivJordradiusM(medRefraksjon);
    return (d0 / rEff) * (180 / Math.PI);
}
/**
 * Avstand (m) til et sjømål gitt total depresjonsvinkel `betaDeg` (fra sann
 * horisontal ned til målets vannlinje — IKKE bare gapet til horisonten, se
 * `maritimAvstand` som legger dip-vinkelen til gapet) og øyehøyde `hoydeM`.
 *
 * KORTE HOLD: d ≈ h/tan(β) — eksakt flat-jord-trigonometri.
 * MED KRUMNING (konseptdokumentets formel): β = atan(h/d) + d/(2R′). Denne kan
 * ikke løses for d i lukket form, så vi itererer (fixed-point, konvergerer
 * raskt fordi krumningsleddet er lite mot atan-leddet på maritime avstander):
 * start med flat-jord-gjettet, forbedre β → φ = β − d/(2R′), sett d = h/tan(φ).
 *
 * ⚠ SAMME FORTEGN-FELLE SOM `maalestimat.ts` sin `krumningsloft`: krumningen
 * gjør at MER av depresjonen «går til» selve krumningen og MINDRE til den rene
 * atan-vinkelen, så korrigert d blir STØRRE enn flat-jord-gjettet for samme β
 * (nøyaktig som testen `krumningKontroll` i test-fila låser).
 */
export function avstandFraDepresjon(hoydeM, betaDeg, opts = {}) {
    if (hoydeM <= 0 || !(betaDeg > 0))
        return null;
    const betaRad = (betaDeg * Math.PI) / 180;
    const d0 = hoydeM / Math.tan(betaRad);
    if (!(d0 > 0) || !Number.isFinite(d0))
        return null;
    if (opts.krumningsledd === false)
        return d0;
    const rEff = effektivJordradiusM(opts.medRefraksjon ?? true);
    let d = d0;
    for (let i = 0; i < KRUMNING_ITERASJONER; i++) {
        const phi = betaRad - d / (2 * rEff);
        if (!(phi > 0))
            return d; // degenerert (krumningsleddet spiste hele vinkelen) — behold siste gyldige
        const dNy = hoydeM / Math.tan(phi);
        if (!Number.isFinite(dNy) || dNy <= 0)
            return d;
        d = dNy;
    }
    return d;
}
/**
 * Vinkelgapet (grader) mellom horisontlinja og målets vannlinje, LEST I
 * PIKSLER — «vinkelen mellom horisont og vannlinje leses direkte i piksler»
 * (konseptdok §1). Bruker `vinkelFraPikselRad` (kamerakalibrering.ts) på BEGGE
 * radene fra SAMME kalibrering: monteringens pitch/roll kanselleres i
 * differansen, så metoden er uavhengig av hvordan telefonen holdes — nettopp
 * konseptdokumentets poeng. `null` når `vfovDeg`/`hoydePx` ikke er kjent, eller
 * når målet er over horisontlinja i bildet (se `maritimAvstand` for hvordan det
 * tilfellet avvises i stedet for å late som).
 */
export function gapDegFraPiksler(vfovDeg, hoydePx, horisontYPiksel, malYPiksel) {
    const vHorisont = vinkelFraPikselRad({ vfovDeg, hoydePx }, horisontYPiksel);
    const vMal = vinkelFraPikselRad({ vfovDeg, hoydePx }, malYPiksel);
    if (vHorisont === null || vMal === null)
        return null;
    return vHorisont - vMal;
}
/**
 * Horisont + havflate: avstand til et sjømål UTEN DEM, UTEN IMU-pitch. Se
 * filhodet og konseptdokumentets §1. `gapDeg` skal komme fra et faktisk
 * pikselavlest gap mellom horisontlinje og målets vannlinje i SAMME bilde
 * (UI-jobben er å la operatøren klikke begge — automatisk horisont-DETEKSJON
 * i dis/motlys/skipstrafikk er UPRØVD, jf. konseptdokumentets §6, og bygges
 * ikke her: den tar inn en allerede lest vinkel, ikke et rått bilde).
 */
export function maritimAvstand(input) {
    const METODE = 'maritim-horisont';
    if (!(input.hoydeM > 0) || !Number.isFinite(input.gapDeg)) {
        return {
            kind: 'avvist',
            metode: METODE,
            grunn: 'ugyldig-inndata',
            forklaring: 'Øyehøyde må være > 0, og gapet mellom horisont og vannlinje må være et tall.',
        };
    }
    if (input.gapDeg < 0) {
        return {
            kind: 'avvist',
            metode: METODE,
            grunn: 'mal-over-horisont',
            forklaring: 'Målets vannlinje ligger OVER horisontlinja i bildet — da finnes intet ' +
                'sjøoverflatepunkt strålen kan treffe (målet er bak horisonten, eller ' +
                'klikkene er forvekslet). Ingen avstand kan regnes.',
        };
    }
    const Hs = input.bolgehoydeSignifikantM;
    if (Hs !== undefined && Hs > 0 && input.hoydeM < BOLGE_HOYDE_FORHOLD_MIN * Hs) {
        return {
            kind: 'avvist',
            metode: METODE,
            grunn: 'bolger-for-store',
            forklaring: `Øyehøyde ${input.hoydeM.toFixed(1)} m er under ${BOLGE_HOYDE_FORHOLD_MIN}× signifikant ` +
                `bølgehøyde (${Hs.toFixed(1)} m). Horisonten defineres da av bølgetopper, ikke ` +
                'middelvannflaten, og gir en SYSTEMATISK overestimert avstand — konseptdokumentets ' +
                'egen regel er å avvise målingen i stedet for å vise et tall som lyver i en kjent retning.',
        };
    }
    const dipDeg = horisontDipDeg(input.hoydeM, input.medRefraksjon ?? true);
    const betaDeg = dipDeg + input.gapDeg;
    const avstandM = avstandFraDepresjon(input.hoydeM, betaDeg, input);
    if (avstandM === null) {
        return {
            kind: 'avvist',
            metode: METODE,
            grunn: 'ugyldig-inndata',
            forklaring: 'Depresjonsvinkelen ga ingen gyldig avstand (geometrien er degenerert).',
        };
    }
    // σ: numerisk, ved å forstyrre de to UAVHENGIGE inngangene (høyde, pikselgap)
    // hver for seg og kombinere i kvadratur — samme resonnement som
    // `estimerMaal` i maalestimat.ts («skyt ekstra stråler» i stedet for en
    // lukket feilformel som bare gjelder i grensetilfellet).
    const hoydeSigma = input.hoydeSigmaM ?? 0;
    const gapSigma = input.gapSigmaDeg ?? 0;
    // Bølgeindusert vannlinje-usikkerhet på MÅLET (±Hs/2, konseptdok §1) —
    // omregnet til et vinkelekvivalent ved senteravstanden, og lagt i kvadratur
    // til pikselgapets egen usikkerhet (uavhengige kilder: fitt-presisjon vs.
    // bølgens fysiske forskyvning av selve vannlinja).
    const bolgeVinkelSigma = Hs !== undefined && Hs > 0 ? (Math.atan(Hs / 2 / avstandM) * 180) / Math.PI : 0;
    const gapSigmaTotal = Math.sqrt(gapSigma * gapSigma + bolgeVinkelSigma * bolgeVinkelSigma);
    let sigmaKvadrSum = 0;
    if (hoydeSigma > 0) {
        const avvik = maksAvvikNumerisk((h) => avstandFraDepresjon(h, horisontDipDeg(h, input.medRefraksjon ?? true) + input.gapDeg, input), input.hoydeM, hoydeSigma, avstandM);
        sigmaKvadrSum += avvik * avvik;
    }
    if (gapSigmaTotal > 0) {
        const avvik = maksAvvikNumerisk((gap) => avstandFraDepresjon(input.hoydeM, dipDeg + gap, input), input.gapDeg, gapSigmaTotal, avstandM);
        sigmaKvadrSum += avvik * avvik;
    }
    const sigmaM = Math.sqrt(sigmaKvadrSum);
    const advarsler = [];
    if (hoydeSigma === 0 && gapSigmaTotal === 0) {
        advarsler.push('Ingen usikkerhet oppgitt for øyehøyde eller pikselgap — σ = 0 betyr «ikke oppgitt», ikke «perfekt kjent».');
    }
    if (Hs !== undefined && Hs > 0) {
        advarsler.push(`Bølger (Hs ${Hs.toFixed(1)} m) forskyver horisontfittet systematisk mot LENGRE avstand ` +
            '(bølgetopper, ikke middelvann) — denne skjevheten er IKKE korrigert, bare σ-utvidet.');
    }
    return {
        kind: 'ok',
        avstandM,
        sigmaM,
        metode: METODE,
        dipDeg,
        betaDeg,
        forklaring: `Horisont+havflate: dip ${dipDeg.toFixed(3)}° (øyehøyde ${input.hoydeM.toFixed(1)} m) + ` +
            `pikselgap ${input.gapDeg.toFixed(3)}° = depresjon ${betaDeg.toFixed(3)}° → ${Math.round(avstandM)} m. ` +
            'Ingen høydemodell brukt — ren horisontgeometri.',
        advarsler,
    };
}
/** Finn STØRSTE avvik fra `senter` når `f` evalueres ved x₀±σ. `null` fra `f`
 *  (ugyldig kant) telles som «minst hele senterverdien», samme prinsipp som
 *  `estimerMaal` i maalestimat.ts: en kant som ikke gir svar er ikke en liten
 *  usikkerhet, det er en STOR en. */
function maksAvvikNumerisk(f, x0, sigma, senter) {
    let maks = 0;
    for (const dx of [-sigma, sigma]) {
        const v = f(x0 + dx);
        maks = Math.max(maks, v === null ? senter : Math.abs(v - senter));
    }
    return maks;
}
// ═══════════════════════════════════════════════════════════════════════════
// 3. LANDEMERKE-RESECTION — asimut fra ett+ kjente punkt i bildet
// ═══════════════════════════════════════════════════════════════════════════
/** Telefonkompass alene, siste utvei i konseptdokumentets prioriterte liste. */
export const MAGNETOMETER_SIGMA_DEG_DEFAULT = 20;
/**
 * 1σ pikselusikkerhet i hvor presist et landemerke er identifisert i bildet
 * (kant vs. midtpunkt, dis, kompresjonsartefakter). DETTE ER ET ANSLAG, IKKE
 * EN MÅLING — akkurat som `kExifRetningSigmaDeg = 5°` i GNÅ (konseptdok §6),
 * og skal overstyres når feltet gir bedre tall. Ærlig merket, ikke gjemt som
 * en «magisk» default.
 */
export const DEFAULT_LANDEMERKE_PIKSEL_SIGMA_PX = 2;
/**
 * Boresight-asimuten (monterings-/kamera-midtaksens sanne peiling) som gjør at
 * ETT landemerke med kjent posisjon projiserer til DEN pikselkolonnen det ble
 * klikket på. Dette er `vinkelFraPikselKolonne` (kamerakalibrering.ts) i
 * REVERS: normalt vet vi asimut og søker peilingen til en piksel; her vet vi
 * peilingen (fra kjent posisjon) og søker asimuten.
 */
function boresightFraLandemerke(kamera, lm, breddePx, hfovDeg) {
    const avstandM = haversineM(kamera, lm);
    const sannPeiling = bearingDeg(kamera, lm);
    const vinkel = vinkelFraPikselKolonne({ breddePx, hfovDeg }, lm.xPiksel);
    const boresightDeg = peiling360(sannPeiling - vinkel);
    const pikselSigma = lm.xPikselSigma ?? DEFAULT_LANDEMERKE_PIKSEL_SIGMA_PX;
    const vinkelVedPluss = vinkelFraPikselKolonne({ breddePx, hfovDeg }, lm.xPiksel + pikselSigma);
    const vinkelVedMinus = vinkelFraPikselKolonne({ breddePx, hfovDeg }, lm.xPiksel - pikselSigma);
    const sigmaDeg = Math.max(Math.abs(vinkelVedPluss - vinkel), Math.abs(vinkelVedMinus - vinkel));
    return { navn: lm.navn, avstandM, boresightDeg, sigmaDeg };
}
/** Minste vinkeldifferanse a→b, i (−180,180]. */
function vinkelDiffDeg(a, b) {
    const d = ((b - a + 540) % 360) - 180;
    return d;
}
/**
 * Landemerke-resection: løs kameraets boresight-asimut fra ett eller flere
 * KJENTE, i bildet MANUELT identifiserte punkt (operatøren peker — dette er
 * IKKE bildegjenkjenning/feature-matching, som ikke finnes i denne kodebasen
 * og ikke er bygget her, se VALE-rapporten til denne bølgen for hvorfor).
 *
 * MED ÉTT LANDEMERKE: boresight = landemerkets estimat direkte, σ fra
 * pikselusikkerheten alene — ingen tverrkontroll, og det sies eksplisitt.
 * MED FLERE: kombinert med invers-varians-vekting (mer presise landemerker
 * (fjernere = mindre vinkel-per-piksel-feil, eller lavere oppgitt σ) veier
 * tyngre), men σ blir ALDRI mindre enn halve spredningen mellom dem — stemmer
 * landemerkene dårligere med hverandre enn pikkelfeilen predikerer, er DET
 * den ærlige usikkerheten, ikke den optimistiske kombinerte variansen.
 */
export function losResection(input) {
    if (input.landemerker.length === 0) {
        return {
            kind: 'ingen-landemerke',
            forklaring: 'Ingen landemerker oppgitt — resection krever minst ett identifisert, kjent punkt i bildet.',
        };
    }
    const kamera = { lat: input.kameraLat, lon: input.kameraLon };
    const bidrag = input.landemerker.map((lm) => boresightFraLandemerke(kamera, lm, input.breddePx, input.hfovDeg));
    let asimutDeg;
    let sigmaDeg;
    let spredningDeg = null;
    const advarsler = [];
    if (bidrag.length === 1) {
        asimutDeg = bidrag[0].boresightDeg;
        sigmaDeg = bidrag[0].sigmaDeg;
        advarsler.push('Kun ett landemerke — ingen tverrkontroll av boresighten. Et andre landemerke bekrefter roll/feilklikk.');
    }
    else {
        // Sirkulær invers-varians-veiing (unngår 359°/1°-wrap-feilen en rett gjennomsnitt ville gitt).
        let sx = 0, sy = 0, wSum = 0, wVarSum = 0;
        for (const b of bidrag) {
            const w = 1 / Math.max(b.sigmaDeg, 1e-6) ** 2;
            const rad = (b.boresightDeg * Math.PI) / 180;
            sx += w * Math.cos(rad);
            sy += w * Math.sin(rad);
            wSum += w;
            wVarSum += w;
        }
        asimutDeg = peiling360((Math.atan2(sy, sx) * 180) / Math.PI);
        const kombinertSigma = Math.sqrt(1 / wVarSum);
        let maksAvvik = 0;
        for (let i = 0; i < bidrag.length; i++) {
            for (let j = i + 1; j < bidrag.length; j++) {
                maksAvvik = Math.max(maksAvvik, Math.abs(vinkelDiffDeg(bidrag[i].boresightDeg, bidrag[j].boresightDeg)));
            }
        }
        spredningDeg = maksAvvik;
        // ALDRI rapporter smalere enn spredningen predikerer — jf. cepM-prinsippet i maalestimat.ts.
        sigmaDeg = Math.max(kombinertSigma, spredningDeg / 2);
        if (spredningDeg > 3 * kombinertSigma && spredningDeg > 0.05) {
            advarsler.push(`Landemerkene spriker ${spredningDeg.toFixed(2)}° — mer enn pikselusikkerheten alene ` +
                'skulle gi. σ er derfor satt fra spredningen, ikke fra den (for optimistiske) kombinerte variansen.');
        }
    }
    let magnetometer;
    if (input.magnetometerAsimutDeg !== undefined) {
        const magSigma = input.magnetometerSigmaDeg ?? MAGNETOMETER_SIGMA_DEG_DEFAULT;
        magnetometer = {
            asimutDeg: peiling360(input.magnetometerAsimutDeg),
            sigmaDeg: magSigma,
            avvikDeg: Math.abs(vinkelDiffDeg(asimutDeg, input.magnetometerAsimutDeg)),
        };
    }
    const forbedring = magnetometer ? magnetometer.sigmaDeg / Math.max(sigmaDeg, 1e-6) : null;
    const forklaring = `Landemerke-resection (${bidrag.length} landemerke${bidrag.length > 1 ? 'r' : ''}): ` +
        `${asimutDeg.toFixed(2)}° ± ${sigmaDeg.toFixed(2)}°.` +
        (magnetometer
            ? ` Magnetometer: ${magnetometer.asimutDeg.toFixed(0)}° ± ${magnetometer.sigmaDeg.toFixed(0)}° ` +
                `(avvik ${magnetometer.avvikDeg.toFixed(1)}°, innenfor magnetometerets feilmargin). ` +
                `Resection brukes — den er ${forbedring ? forbedring.toFixed(0) : '?'}× mer presis.`
            : ' Ingen magnetometer-avlesning oppgitt til sammenligning.');
    return {
        kind: 'ok',
        asimutDeg,
        sigmaDeg,
        metode: 'landemerke-resection',
        antallLandemerker: bidrag.length,
        perLandemerke: bidrag,
        spredningDeg,
        magnetometer,
        forklaring,
        advarsler,
    };
}
/**
 * Bruk en løst boresight-asimut til å peile MÅLETS pikselkolonne i samme
 * bilde. σ kombinerer boresightens σ (fra `losResection`) med målklikkets
 * EGEN pikselusikkerhet, i kvadratur (uavhengige kilder).
 */
export function resectionPeilingTilMaal(estimat, breddePx, hfovDeg, malXPiksel, malXPikselSigma = DEFAULT_LANDEMERKE_PIKSEL_SIGMA_PX) {
    const vinkel = vinkelFraPikselKolonne({ breddePx, hfovDeg }, malXPiksel);
    const asimutDeg = peiling360(estimat.asimutDeg + vinkel);
    const vPluss = vinkelFraPikselKolonne({ breddePx, hfovDeg }, malXPiksel + malXPikselSigma);
    const malVinkelSigma = Math.abs(vPluss - vinkel);
    const sigmaDeg = Math.sqrt(estimat.sigmaDeg ** 2 + malVinkelSigma ** 2);
    return { asimutDeg, sigmaDeg };
}
export function fusjonerMaalpeking(kamera, asimut, avstand) {
    const punkt = project(kamera, asimut.asimutDeg, avstand.avstandM);
    const storAkseM = avstand.sigmaM;
    const lilleAkseM = avstand.avstandM * Math.tan((asimut.sigmaDeg * Math.PI) / 180);
    return {
        lat: punkt.lat,
        lon: punkt.lon,
        avstandM: avstand.avstandM,
        asimutDeg: asimut.asimutDeg,
        usikkerhet: {
            storAkseM,
            lilleAkseM,
            retningDeg: asimut.asimutDeg % 180,
            cepM: Math.max(storAkseM, lilleAkseM),
        },
        avstand,
        asimut,
    };
}
//# sourceMappingURL=maalpeking.js.map